import React, { useState, useEffect } from 'react';
import { getCollectionData } from '../../services/dbService';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { ReactComponent as ViticulturaIcon } from '../../assets/icons/ViticulturaIcon.svg';
import { ReactComponent as VinificacionIcon } from '../../assets/icons/VinificacionIcon.svg';
import { ReactComponent as EmbotelladoIcon } from '../../assets/icons/EmbotelladoIcon.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/SearchIcon.svg';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '../LoadingSpinner';
import EmptyState from '../EmptyState';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';

// Mismo patrón que el Tooltip de Stock.js: mide la posición del trigger al
// entrar el mouse y flota el contenido centrado verticalmente (con contenido
// largo, pegarlo abajo del elemento lo cortaba contra el borde de la pantalla).
const Tooltip = ({ content, children, delay = 250 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const ref = React.useRef();
  const timeoutRef = React.useRef();
  const handleMouseEnter = () => {
    const rect = ref.current.getBoundingClientRect();
    setCoords({
      top: rect.top + rect.height / 2 + window.scrollY,
      left: rect.left + rect.width / 2 + window.scrollX,
    });
    timeoutRef.current = setTimeout(() => setIsHovered(true), delay);
  };
  const handleMouseLeave = () => {
    clearTimeout(timeoutRef.current);
    setIsHovered(false);
  };
  return (
    <>
      <div ref={ref} style={{ display: 'inline-block' }} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {children}
      </div>
      {isHovered && (
        <div
          style={{
            position: 'absolute',
            top: coords.top,
            left: coords.left,
            transform: 'translateY(-50%)',
            backgroundColor: '#f9fafd',
            padding: '8px 12px',
            borderRadius: '4px',
            whiteSpace: 'normal',
            zIndex: 1000,
            fontSize: '13px',
            minWidth: '220px',
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          {content}
        </div>
      )}
    </>
  );
};
// Construye el contenido del tooltip de "Partidas usadas" (mismo dato que
// agregamos en Stock): bodega/nombre y hectolitros o botellas de cada
// partida. 'partidas' ya viene enriquecida (ver useEffect en Trazabilidad()).
const PartidasUsadasContent = ({ partidas }) => (
  <div style={{ color: "#2c2c2c", fontSize: "13px", fontFamily: "Effra" }}>
    Partidas usadas:
    {(!partidas || partidas.length === 0) ? (
      <p style={{ color: "#999", margin: "0", fontSize: "13px" }}>-</p>
    ) : (
      partidas.map((p, idx) => (
        <p key={idx} style={{ color: "#999", margin: "0", fontSize: "13px" }}>
          {p.nombre} — {p.bodega} — {p.cantidad}
        </p>
      ))
    )}
  </div>
);
const sumaBotellas = (embotelladosDetalle) =>
  (embotelladosDetalle || []).reduce((acc, l) => acc + (Number(l.cantidad_botellas) || 0), 0);
const DocumentCard = ({ doc, isRecent, isFile, partidasEnriquecidas, cepasPorTraz }) => {
const navigate = useNavigate();
const handleClick = () => {
navigate(`/trazabilidad/${doc.id}`);
};
const isFinalizado = doc.estado === 'finalizado';
const statusText = isFinalizado ? 'Finalizado' : 'En proceso';
const statusColor = isFinalizado ? 'green' : 'yellow';
let fechaFormateada = '';
if (doc.fecha_creacion) {
const dateObj = doc.fecha_creacion.toDate
? doc.fecha_creacion.toDate()
: new Date(doc.fecha_creacion.seconds * 1000);
const dia = String(dateObj.getDate()).padStart(2, '0');
const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
const anio = dateObj.getFullYear();
const horas = String(dateObj.getHours()).padStart(2, '0');
const minutos = String(dateObj.getMinutes()).padStart(2, '0');
fechaFormateada = `El ${dia}/${mes}/${anio} a las ${horas}:${minutos}`;
}
const getColeccionIcon = () => {
if (!isFile) return null;
switch (doc.coleccion) {
case 'Viticultura':
return <ViticulturaIcon className="coleccion-icon" />;
case 'Vinificacion':
return <VinificacionIcon className="coleccion-icon" />;
case 'Embotellado':
return <EmbotelladoIcon className="coleccion-icon" />;
case 'Trazabilidad':
return <SearchIcon className="coleccion-icon" />;
default:
return null;
}
};
// Contenido del tooltip del código: sólo para Embotellado y Trazabilidad
// (ver conversación) — el resto de las colecciones no lleva tooltip.
const tooltipContent = (() => {
if (doc.coleccion === 'Embotellado') {
return (
<div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
<div style={{ color: "#2c2c2c", fontSize: "13px", fontFamily: "Effra" }}>
Botellas embotelladas:
<p style={{ color: "#999", margin: "0", fontSize: "13px" }}>
{sumaBotellas(doc.embotellados_detalle).toLocaleString("es-AR")}
</p>
</div>
<PartidasUsadasContent partidas={partidasEnriquecidas?.[doc.id]} />
<div style={{ color: "#2c2c2c", fontSize: "13px", fontFamily: "Effra" }}>
Creado por:
<p style={{ color: "#999", margin: "0", fontSize: "13px" }}>{doc.creador || "-"}</p>
</div>
</div>
);
}
if (doc.coleccion === 'Trazabilidad') {
if (doc.marca) {
// Vino ya etiquetado: lo relevante acá es qué vino salió de esta
// trazabilidad, no de dónde viene (eso ya se ve en Embotellado).
const { cepas = [], anada } = cepasPorTraz?.[doc.id] || {};
const cepaYAnio = [cepas.length ? cepas.join(", ") : null, anada || null].filter(Boolean).join(", ");
return (
<div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
<div style={{ color: "#2c2c2c", fontSize: "13px", fontFamily: "Effra" }}>
Etiqueta:
<p style={{ color: "#999", margin: "0", fontSize: "13px" }}>{[doc.marca, doc.variedad].filter(Boolean).join(" ")}</p>
<p style={{ color: "#999", margin: "0", fontSize: "13px" }}>{cepaYAnio || "-"}</p>
</div>
<div style={{ color: "#2c2c2c", fontSize: "13px", fontFamily: "Effra" }}>
Botellas:
<p style={{ color: "#999", margin: "0", fontSize: "13px" }}>
{sumaBotellas(doc.embotellados_detalle).toLocaleString("es-AR")}
</p>
</div>
</div>
);
}
// Todavía no se etiquetó: lo útil es de qué embotellado viene y qué
// partidas lo componen, igual que en el tooltip de Embotellado/Stock.
return (
<div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
<div style={{ color: "#2c2c2c", fontSize: "13px", fontFamily: "Effra" }}>
Embotellado:
<p style={{ color: "#999", margin: "0", fontSize: "13px" }}>{doc.embotellado_id || "-"}</p>
</div>
<PartidasUsadasContent partidas={partidasEnriquecidas?.[doc.id]} />
</div>
);
}
return null;
})();
if (isFile) {
const modificador = doc.modificador || '';
const fechaModificacion = doc.fecha_modificacion
? doc.fecha_modificacion.toDate
? doc.fecha_modificacion.toDate()
: new Date(doc.fecha_modificacion.seconds * 1000)
: null;
let fechaModFormateada = '';
if (fechaModificacion) {
const dia = String(fechaModificacion.getDate()).padStart(2, '0');
const mes = String(fechaModificacion.getMonth() + 1).padStart(2, '0');
const anio = fechaModificacion.getFullYear();
const horas = String(fechaModificacion.getHours()).padStart(2, '0');
const minutos = String(fechaModificacion.getMinutes()).padStart(2, '0');
fechaModFormateada = `El ${dia}/${mes}/${anio} a las ${horas}:${minutos}`;
}
return (
<div className="file-item" onClick={handleClick}>
<span className="file-id" style={{paddingLeft:'10px'}}>
{getColeccionIcon()} {tooltipContent ? (
<Tooltip content={tooltipContent}>
<span>{doc.id}</span>
</Tooltip>
) : doc.id}
</span>
<span className="file-date">
<strong>{doc.creador}</strong>
<br />
{fechaFormateada}
</span>
<span className="file-status">
<span className={`status-circle ${statusColor}`}></span>
<span className="status-text">{statusText}</span>
</span>
<span className="file-modificado">
<strong>{modificador}</strong>
<br />
{fechaModFormateada}
</span>
</div>
);
}
return (
<div className={`document-card ${isRecent ? 'recent-card' : ''}`} onClick={handleClick}>
<span className={`status-circle ${statusColor}`}></span>
<div className="card-header">
<span className="doc-id">
{tooltipContent ? <Tooltip content={tooltipContent}><span>{doc.id}</span></Tooltip> : doc.id}
</span>
{doc.creador && <p className="card-creator">{doc.creador}</p>}
{fechaFormateada && <p className="card-date">{fechaFormateada}</p>}
</div>
</div>
);
};
// Fuera del componente para que sobreviva a que se desmonte al navegar (ej.
// al editar un lote y volver): guarda la última lista bajada de Firestore
// para poder mostrarla de entrada la próxima vez, en vez de arrancar siempre
// en blanco con el spinner tapando la pantalla mientras se vuelve a bajar.
let trazabilidadCache = null;
function Trazabilidad() {
const [allDocs, setAllDocs] = useState(trazabilidadCache || []);
const [loading, setLoading] = useState(!trazabilidadCache);
const [error, setError] = useState(null);
const [selectedCollection, setSelectedCollection] = useState("Todos");
const [sortColumn, setSortColumn] = useState("fecha_creacion");
const [sortDirection, setSortDirection] = useState("desc");

// Ahora la lista de colecciones arranca con "Todos"
const colecciones = ["Todos", "Viticultura", "Vinificacion", "Embotellado", "Trazabilidad"];

const handleSort = (column) => {
if (sortColumn === column) {
setSortDirection(sortDirection === "asc" ? "desc" : "asc");
} else {
setSortColumn(column);
setSortDirection("asc");
}
};

useEffect(() => {
const fetchData = async () => {
try {
const [viticultura, vinificacion, embotellado, trazabilidad] = await Promise.all([
getCollectionData("viticultura"),
getCollectionData("vinificacion"),
getCollectionData("embotellado"),
getCollectionData("trazabilidad"),
]);
const viticulturaDocs = viticultura.map((doc) => ({ ...doc, coleccion: "Viticultura" }));
const vinificacionDocs = vinificacion.map((doc) => ({ ...doc, coleccion: "Vinificacion" }));
const embotelladoDocs = embotellado.map((doc) => ({ ...doc, coleccion: "Embotellado" }));
const trazabilidadDocs = trazabilidad.map((doc) => ({ ...doc, coleccion: "Trazabilidad" }));
const combinedDocs = [
...viticulturaDocs,
...vinificacionDocs,
...embotelladoDocs,
...trazabilidadDocs,
];
setAllDocs(combinedDocs);
// Se guarda para que la próxima vez que se entre a esta pantalla (ej.
// volviendo de editar un lote) se muestre esta lista al toque, en vez de
// tapar todo con el spinner mientras se recarga en segundo plano.
trazabilidadCache = combinedDocs;
setLoading(false);
} catch (err) {
setError("No se pudieron cargar los datos de trazabilidad.");
setLoading(false);
console.error(err);
}
};
fetchData();
}, []);

// Partidas usadas por cada Embotellado / Trazabilidad sin etiquetar: viven
// en el propio doc ('partidas_usadas'), pero las de origen 'vinificacion'
// (vino propio, no comprado) no traen bodega ahí -- eso vive en el doc de
// la partida misma, así que se busca aparte (mismo patrón que en Stock).
const [partidasEnriquecidas, setPartidasEnriquecidas] = useState({});
useEffect(() => {
const relevantes = allDocs
.filter((d) => d.coleccion === 'Embotellado' || (d.coleccion === 'Trazabilidad' && !d.marca))
.filter((d) => !partidasEnriquecidas[d.id]);
if (relevantes.length === 0) return;
const fetchPartidas = async () => {
try {
const refsVinificacion = new Set();
relevantes.forEach((d) => {
(d.partidas_usadas || []).forEach((p) => {
if (p.origen === 'vinificacion' && p.ref) refsVinificacion.add(p.ref);
});
});
const listaRefs = [...refsVinificacion];
const vinificacionDocs = await Promise.all(
listaRefs.map((ref) => getDoc(doc(db, 'vinificacion', ref)))
);
const bodegaPorRef = {};
vinificacionDocs.forEach((snap, idx) => {
bodegaPorRef[listaRefs[idx]] = snap.exists() ? snap.data().nombre_bodega : null;
});
const resultado = {};
relevantes.forEach((d) => {
resultado[d.id] = (d.partidas_usadas || []).map((p) => ({
nombre: p.origen === 'vinificacion' ? p.ref : (p.nombre_stock || p.ref),
bodega: p.origen === 'vinificacion' ? (bodegaPorRef[p.ref] || '-') : (p.bodega || '-'),
cantidad: p.ya_embotellado
? `${p.cantidad_botellas ?? '-'} botellas`
: `${p.cantidad_hl ?? '-'} hl`,
}));
});
setPartidasEnriquecidas((prev) => ({ ...prev, ...resultado }));
} catch (error) {
console.error('Error al traer partidas usadas:', error);
}
};
fetchPartidas();
}, [allDocs, partidasEnriquecidas]);

// Cepas y añada del vino ya etiquetado: 'trazabilidad' no las guarda (sólo
// viven en el doc de 'vinos' que se crea al etiquetar -- ni 'anada' ni
// 'cepas' se copian ahí, ver /crear-vinos), así que se buscan por
// 'trazabilidad_id' recién cuando esa trazabilidad ya tiene marca.
const [cepasPorTraz, setCepasPorTraz] = useState({});
useEffect(() => {
const trazFinalizadas = allDocs
.filter((d) => d.coleccion === 'Trazabilidad' && d.marca)
.filter((d) => !cepasPorTraz[d.id]);
if (trazFinalizadas.length === 0) return;
const fetchCepas = async () => {
try {
const resultados = await Promise.all(
trazFinalizadas.map(async (d) => {
const q = query(collection(db, 'vinos'), where('trazabilidad_id', '==', d.id));
const snap = await getDocs(q);
const vinoData = snap.docs.length > 0 ? snap.docs[0].data() : {};
return [d.id, { cepas: vinoData.cepas || [], anada: vinoData.anada || null }];
})
);
setCepasPorTraz((prev) => {
const next = { ...prev };
resultados.forEach(([id, datos]) => { next[id] = datos; });
return next;
});
} catch (error) {
console.error('Error al traer cepas:', error);
}
};
fetchCepas();
}, [allDocs, cepasPorTraz]);

// Recientes (siempre por fecha desc)
const recentDocs = [...allDocs]
.sort((a, b) => {
const dateA = a.fecha_creacion?.toDate
? a.fecha_creacion.toDate()
: new Date(a.fecha_creacion?.seconds * 1000);
const dateB = b.fecha_creacion?.toDate
? b.fecha_creacion.toDate()
: new Date(b.fecha_creacion?.seconds * 1000);
return dateB - dateA;
})
.slice(0, 4);

// Archivos filtrados + ordenados
const sortedAndFilteredDocs = [...allDocs]
.filter((doc) => selectedCollection === "Todos" || doc.coleccion === selectedCollection)
.sort((a, b) => {
const isAsc = sortDirection === "asc";
let aValue, bValue;

switch (sortColumn) {
case "id":
const re = /(\d+|\D+)/g;
const aParts = (a.id || "").match(re) || [];
const bParts = (b.id || "").match(re) || [];
const len = Math.min(aParts.length, bParts.length);
for (let i = 0; i < len; i++) {
const aPart = aParts[i];
const bPart = bParts[i];
const isANumeric = /^\d+$/.test(aPart);
const isBNumeric = /^\d+$/.test(bPart);
if (isANumeric && isBNumeric) {
const numA = parseInt(aPart, 10);
const numB = parseInt(bPart, 10);
if (numA !== numB) {
return isAsc ? numA - numB : numB - numA;
}
} else if (!isANumeric && !isBNumeric) {
const compareResult = aPart.localeCompare(bPart);
if (compareResult !== 0) {
return isAsc ? compareResult : -compareResult;
}
} else {
return isAsc ? (isANumeric ? 1 : -1) : isANumeric ? -1 : 1;
}
}
return isAsc ? aParts.length - bParts.length : bParts.length - aParts.length;

case "creador":
aValue = a.creador || "";
bValue = b.creador || "";
return isAsc ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);

case "estado":
aValue = a.estado || "";
bValue = b.estado || "";
return isAsc ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);

case "modificador":
aValue = a.modificador || "";
bValue = b.modificador || "";
return isAsc ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);

case "fecha_creacion":
default:
const dateA = a.fecha_creacion?.toDate
? a.fecha_creacion.toDate()
: new Date(a.fecha_creacion?.seconds * 1000);
const dateB = b.fecha_creacion?.toDate
? b.fecha_creacion.toDate()
: new Date(b.fecha_creacion?.seconds * 1000);
return isAsc ? dateA - dateB : dateB - dateA;
}
});

if (loading) return <LoadingSpinner />;
if (error) return <div className="error-state">{error}</div>;

return (
<div className="Trazabilidad">
<header className="trazabilidad-header">
<div className="header-content">
<h1>Trazabilidad</h1>
</div>
</header>
{allDocs.length === 0 ? (
<EmptyState label="registros de trazabilidad" />
) : (
<div className="trazabilidad-content">
<div className="section">
<h2>Recientes</h2>
<div className="recent-grid">
{recentDocs.length > 0 ? (
recentDocs.map((doc) => (
<DocumentCard key={doc.id} doc={doc} isRecent={true} partidasEnriquecidas={partidasEnriquecidas} cepasPorTraz={cepasPorTraz} />
))
) : (
<p>No hay documentos recientes.</p>
)}
</div>
</div>
<div className="section">
<div className="files-controls">
<h2 style={{margin:'0'}}>Archivos</h2>
<div className="filter-select-container">
<Listbox value={selectedCollection} onChange={setSelectedCollection}>
{({ open }) => (
<>
<ListboxButton className={`sorter-listbox-button ${open ? "open" : ""}`}>
Filtrar por
<span className="sorter-arrow-container">
<span className="sorter-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="sorter-listbox-options">
{colecciones.map((coleccion) => (
<ListboxOption
key={coleccion}
value={coleccion}
className="sorter-listbox-option"
>
{coleccion}
</ListboxOption>
))}
</ListboxOptions>
</>
)}
</Listbox>
</div>
</div>
<div className="files-header">
<span
className={`file-id sortable ${sortColumn === 'id' ? 'active' : ''}`}
onClick={() => handleSort('id')}
>
Código {sortColumn === 'id' && (
<span className={`arrow ${sortDirection}`}></span>
)}
</span>
<span
className={`file-date sortable ${sortColumn === 'creador' ? 'active' : ''}`}
onClick={() => handleSort('creador')}
>
Creado por {sortColumn === 'creador' && (
<span className={`arrow ${sortDirection}`}></span>
)}
</span>
<span
className={`file-status sortable ${sortColumn === 'estado' ? 'active' : ''}`}
onClick={() => handleSort('estado')}
>
Estado {sortColumn === 'estado' && (
<span className={`arrow ${sortDirection}`}></span>
)}
</span>
<span
className={`file-modificado sortable ${sortColumn === 'modificador' ? 'active' : ''}`}
onClick={() => handleSort('modificador')}
>
Modificado por {sortColumn === 'modificador' && (
<span className={`arrow ${sortDirection}`}></span>
)}
</span>
</div>
<div className="files-list">
{sortedAndFilteredDocs.length > 0 ? (
sortedAndFilteredDocs.map((doc) => (
<DocumentCard key={doc.id} doc={doc} isFile={true} partidasEnriquecidas={partidasEnriquecidas} cepasPorTraz={cepasPorTraz} />
))
) : (
<p>No hay documentos para mostrar en esta colección.</p>
)}
</div>
</div>
</div>
)}
</div>
);
}
export default Trazabilidad;